import React, { useState, useEffect, useMemo } from 'react';
import '../css/characterSelector.css';

import LRUCache from '../components/cache.js';
import renderStats from '../components/renderStats';

const artifactCache = new LRUCache(30);
const ArtifactWrapper = ({getArtifactData: passArtifactData, context }) => {
  const [artifactName, setArtifactName] = useState("No Turning Back");
  const [starCount, setStarCount] = useState(5);
  const [artifactData, setArtifactData] = useState(null);
  const [artifactList, setArtifactList] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);


  // Get details of selected artifact
  async function fetchSelectedArtifact(artifactName, stars) {
    const cacheKey = JSON.stringify({ name: artifactName, stars });
    const cached = artifactCache.get(cacheKey);
    if (cached) return cached;

    try {
      const res = await fetch('/api/artifact/getArtifact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: cacheKey
      });

      if (!res.ok) {
        console.error('Failed to fetch artifact data');
        return null;
      }

      const data = await res.json();
      artifactCache.set(cacheKey, data);
      return data;

    } catch (err) {
      console.error('Error fetching artifact:', err);
      return null;
    }
  }

  // Get list of all artifacts
  async function fetchAllArtifacts() {
    const res = await fetch('/api/artifact/getData', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!res.ok) {
      console.error('Failed to fetch artifact list');
      return [];
    }
    return await res.json();
  }
  //Group artifacts by given role, then sort it so the current cookie role appears first
  const groupedArtifacts = useMemo(() => {
    const grouped = Object.entries(artifactList).reduce((acc, [key, artifact]) => {
      const role = artifact.role || "Unknown";
      if (!acc[role]) acc[role] = [];
      acc[role].push([key, artifact]);
      return acc;
    }, {});

    const roleOrder = [context.rarity, context.role, "All"];

    return Object.fromEntries(
      Object.entries(grouped).sort(([a], [b]) => {
        const indexA = roleOrder.indexOf(a);
        const indexB = roleOrder.indexOf(b);

        if (indexA !== -1 && indexB === -1) return -1;
        if (indexB !== -1 && indexA === -1) return 1;
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        return a.localeCompare(b);
      })
    );
  }, [artifactList, context.role, context.rarity]);



  useEffect(() => {
    // Load list of all artifacts
    fetchAllArtifacts().then(setArtifactList);
  }, []);

  useEffect(() => {
    // Load selected artifact data
    fetchSelectedArtifact(artifactName, starCount).then(data => {
      if (data) setArtifactData(data);
    });
  }, [artifactName, starCount]);

  const grabImg = (folder, subfolder, imgName) => {
    const path = subfolder.replace(/ /g, "_");
    //Artifacts don't have a subfolder
    if (imgName === "_Thumbnail") {
    return `/img/${folder}/${path}${imgName}.webp`;
    }
    else {
      return `/img/${folder}/${path}/${imgName}.webp`;
    }
  };

  const updateStarCount = (newCount) => {
    const clamped = Math.max(1, Math.min(5, newCount));
    setStarCount(clamped);
  };

useEffect(() => { 
    passArtifactData(artifactData);
}, [artifactData]) 

  return (
    <span className="characterWrapper">
      {/* Display if artifact data is available */}
      {artifactData ? (
        <>
          {/* artifact name and image */}
        <div className={`characterContainer rarityBgSoft rarityBorder ${artifactData.rarity.toLowerCase()} artifact`}>
          <div className={`characterHeader rarityBorder ${artifactData.rarity.toLowerCase()}`}>Artifact</div>
          <div className="selectedName">{artifactData.name}</div>
          <img
            src={grabImg("Artifact", artifactData.name, "_Thumbnail")}
            alt={artifactData.name}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/img/Artifact/default.webp";
            }}
            className={`artifactImage rarityBorder ${artifactData.rarity.toLowerCase()}`}
            onClick={() => setDropdownOpen(true)} // Open modal on image click
            style={{ cursor: 'pointer' }}
          />
          {/* Star display and increments */}
          <div className="starControls">
            <button onClick={() => updateStarCount(starCount - 1)}>−</button>
            {[...Array(5)].map((_, index) => (
              <img 
              src="/img/Icons/Other/Star.webp"
              key={index}
              className={`star ${index < starCount ? `filled` : 'empty'}`}
              ></img>
            ))}
            <button onClick={() => updateStarCount(starCount + 1)}>+</button>
          </div>
          <div className="characterSelectorLabel">
              Stars: {starCount}/5
          </div>
          
          <div className={`artifact rarityBorder rarityBgDark baseStat ${artifactData.rarity.toLowerCase()}`}>
            Base Stats
          </div>
          <span className="statColorOverride artifactStat">
            {renderStats(artifactData.base)}
          </span>
          



          {artifactData.role === "All" || artifactData.role === context.role || artifactData.cookie === context.name ? (
            <>
            <span className={`roleDisplay rarityBorder rarityBgDark ${artifactData.rarity.toLowerCase()}`}>
            {artifactData.role === "Legendary" 
              ? `${artifactData.cookie} Buffs` 
              : `${artifactData.role} Role Buffs`}
            </span>
            {artifactData.buffs.map((buff) => (
              <div className="buffItem" key={buff.name}>
                <div className="buffHeader">
                  <span className="buffName">-{buff.name}</span>
                  {buff.stackLimit && (
                    <span className={`buffStackLimit rarityText ${artifactData.rarity.toLowerCase()}`}>Max: {buff.stackLimit}</span>
                  )}
                </div>

                <div className="statColorOverride buffStats">
                  {renderStats(buff.stats)}
                </div>
              </div>
            ))}

            {artifactData.attacks && Object.keys(artifactData.attacks).length > 0 ? (
              <>
              <span className={`roleDisplay rarityBorder rarityBgDark ${artifactData.rarity.toLowerCase()}`}>
              {artifactData.cookie} Extra Bonus
              </span>
                {Object.entries(artifactData.attacks).map(([categoryName, attacks]) => {
                  const displayName = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
                  return (
                  <div className="AttackCategory" key={`${categoryName}`}>
                    {attacks.map((attack) => (
                      <div className="buffItem" key={`${attack.name}`}>
                        <div className="buffHeader">
                          <span className="buffName">-Adds {displayName} attack "{attack.name}"</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  );  
})}
              </>
            ) : null}
            </>

          ) : (
            <>
            <span className={`roleDisplay rarityBorder rarityBgDark ${artifactData.rarity.toLowerCase()} failed`}>
            {artifactData.role} Role Buffs
            </span>
            <div className="classBuffWarning">
              Cookie is incompatable with this artifact, no class buffs are applied
            </div>
            </>
          )}
          
        </div>
        </>
      ) : (
        "Loading..."
      )}


      {/* selection modal */}
      {dropdownOpen && (
        <div className="cookieModalOverlay" onClick={() => setDropdownOpen(false)}>
          <div className="cookieModalContent" onClick={(e) => e.stopPropagation()}>
            <div className="cookieDropdown" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {Object.entries(groupedArtifacts).map(([role, items]) => (
              <div key={role} style={{ width: '100%' }}>
                <div className='groupLabel'>
                    <img src={
                      grabImg("Icons", "Role", role)} 
                      className={`groupIcon role`}
                      alt={role}>
                    </img>
                    {role}
                    </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {items.map(([key, artifact]) => (
                    <div
                      key={key}
                      className="artifactImageWrapper"
                      style={{
                        border: key === artifactName ? '2px solid green' : '0px white',
                        borderRadius: '6px'
                      }}
                    >
                      <img
                        src={grabImg("Artifact", key, "_Thumbnail")}
                        alt={artifact.name}
                        className={`dropdownImage rarityBorder ${artifact.rarity?.toLowerCase()}`}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/img/Artifact/default.webp";
                        }}
                        style={{
                          filter: artifact.role !== "All" && artifact.role !== context.role && artifact.cookie !== context.name ? "grayscale(80%)" : "none",
                        }}
                        onClick={() => {
                          setArtifactName(key);
                          setDropdownOpen(false);
                        }}
                      />
                      <div className="nameTooltip" style={{ fontSize: '0.8rem', textAlign: 'center' }}>
                        {artifact.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}


            </div>
          </div>
        </div>
      )}
    </span>
  );
};

export default ArtifactWrapper;
